import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { routes } from 'Routes';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { invariant } from 'shared/utils/utils';
import { RenderUnsettledUI } from 'shared/components';
import { Header, LwButton, Page, Toolbox } from 'redesign';
import { HqUser } from '@types';
import { useUsersPage } from '../hooks/params/use-users';
import { useDialog } from '../../../../../ogp/providers/dialog-provider';
import { useAlert, userAlerts } from '../../../../../shared/components/alerts';
import { useDeleteHqUser } from '../hooks/query/use-delete-user';
import { useGetUser } from '../hooks/query/use-get-user';

const env = import.meta.env.VITE_ENV;

export const UserDetail = () => {
  const { id } = useParams<'id'>();
  invariant(id);
  const userQuery = useGetUser(id);
  if (userQuery.status !== 'success') {
    return <RenderUnsettledUI data={userQuery} />;
  }

  return <UserDetailImpl data={userQuery.data} />;
};

const UserDetailImpl = ({ data }: { data: HqUser }) => {
  const { alertSuccess, alertError } = useAlert();
  const generatePath = useHqRelativePath();
  const navigate = useNavigate();
  const { filters } = useUsersPage();
  const { openDialog, closeDialog, disableDialog, enableDialog } = useDialog();
  const { t } = useTranslation(['hq-screen-specific', 'web-common']);

  const deleteHqUser = useDeleteHqUser();

  const openDeleteDialog = () => {
    openDialog(
      {
        title: t('users.detail.delete_dialog.title', { ns: 'hq-screen-specific' }),
        content:
          env === 'development' || env === 'test'
            ? t('users.detail.delete_dialog.content_dev', { ns: 'hq-screen-specific' })
            : t('users.detail.delete_dialog.content_prod', { ns: 'hq-screen-specific' }),
      },
      () => {
        disableDialog();
        deleteHqUser.mutate(data.id, {
          onSuccess: () => {
            closeDialog();
            alertSuccess(userAlerts.success.deleted);
            navigate(generatePath(routes.Companies));
          },
          onError: (e) => alertError(e),
          onSettled: () => enableDialog(),
        });
      }
    );
  };

  return (
    <Page
      header={<Header titleText={t('users.detail.page_title', { ns: 'hq-screen-specific' })} />}
      toolbox={
        <Toolbox>
          <LwButton color="secondary" to={generatePath(routes.HQUsers, undefined, filters)}>
            {t('buttons.back', { ns: 'web-common' })}
          </LwButton>
          <LwButton to={generatePath(routes.HQUsersEdit, { id: data.id }, filters)}>
            {t('users.detail.edit_button', { ns: 'hq-screen-specific' })}
          </LwButton>
          <LwButton color="primary" onClick={openDeleteDialog}>
            {t('users.detail.delete_button', { ns: 'hq-screen-specific' })}
          </LwButton>
        </Toolbox>
      }
    >
      <Box>
        <Grid container spacing={6}>
          <Grid item xs={4}>
            <Typography variant="subtitle1">
              {t('users.detail.fields.first_name', { ns: 'hq-screen-specific' })}
            </Typography>
            <Typography variant="body1">{data.firstName}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle1">
              {t('users.detail.fields.last_name', { ns: 'hq-screen-specific' })}
            </Typography>
            <Typography variant="body1">{data.lastName}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle1">
              {t('users.detail.fields.nickname', { ns: 'hq-screen-specific' })}
            </Typography>
            <Typography variant="body1">{data.nickname}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle1">
              {t('users.detail.fields.email', { ns: 'hq-screen-specific' })}
            </Typography>
            <Typography variant="body1">{data.email}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle1">
              {t('users.detail.fields.role', { ns: 'hq-screen-specific' })}
            </Typography>
            <Typography variant="body1">{data.roles?.hqRole}</Typography>
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};
